// URL/title scrubbing for anything handed to GA. Extracted from the tracking
// hook so it can be unit-tested in isolation — see ANALYTICS.md §4 (B4, B10).
//
// The threat is not just "we might send a token deliberately". gtag attaches
// `dl` (document.location.href) and `dr` (document.referrer) to EVERY hit on its
// own, so a one-time token in the URL leaks on the next unrelated event unless we
// override those globally with scrubbed values. Everything here exists to produce
// those safe values.

export const REDACTED = "[redacted]";
export const MASKED_ID = ":id";

// Query keys that must never reach GA. Two groups:
//   - auth material (invite/reset tokens, OAuth codes, raw emails)
//   - free-text search keys — GA4 Enhanced Measurement "Site search" reads these
//     and records their CONTENTS as `search_term` (B10). We disable that setting
//     per stream, but scrub here too so a re-enabled stream can't leak.
export const SENSITIVE_QUERY_KEYS = new Set([
  "token",
  "code",
  "refresh_token",
  "access_token",
  "id_token",
  "email",
  "invite",
  "password",
  "secret",
  "key",
  // referral codes: whoever holds one is credited for signups
  "ref",
  // free-text search (B10)
  "q",
  "s",
  "query",
  "keyword",
  "search",
  "search_term",
]);

/** Redacts the values of sensitive query keys, preserving key order. */
export function sanitizeSearch(search: string): string {
  if (!search) return "";
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  for (const key of Array.from(params.keys())) {
    if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
      params.set(key, REDACTED);
    }
  }
  const out = params.toString();
  // Nothing parsed out → return nothing. Echoing the original string back would be
  // fail-open, and it also made a bare "?" a second, distinct page path in GA.
  return out ? `?${out}` : "";
}

// A path segment that looks like data rather than a route name. We mask by
// pattern rather than validating against an enumerated route allowlist: the route
// list is large and changes per release, so an allowlist would silently rot into
// a blocklist. Pattern-masking instead fails CLOSED on anything id-shaped, and
// has the side benefit of collapsing high-cardinality paths so GA's Pages report
// stays readable.
const ID_LIKE = new RegExp(
  [
    "^\\d+$", //                                  numeric id
    "^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$", // uuid
    "^[0-9a-f]{12,}$", //                         hex/object id
    "^[^/]*@[^/]*$", //                           anything email-shaped
  ].join("|"),
  "i"
);

// Long opaque segments (signed urls, base64 blobs) are masked on length alone.
const MAX_SEGMENT_LEN = 40;

/** Masks id-like and over-long path segments: `/offers/8123/edit` → `/offers/:id/edit`. */
export function sanitizePath(pathname: string): string {
  if (!pathname) return "/";
  const parts = pathname.split("/");
  return parts
    .map((segment, i) => {
      if (!segment) return segment;
      // /join/<referral code> (2026-10-19): the code is a credential, whatever it looks like.
      if (i > 0 && parts[i - 1] === "join" && segment !== "preview") return MASKED_ID;
      const decoded = safeDecode(segment);
      if (ID_LIKE.test(decoded) || decoded.length > MAX_SEGMENT_LEN) return MASKED_ID;
      return segment;
    })
    .join("/");
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    // Malformed percent-encoding — treat the raw value as the thing to inspect.
    return value;
  }
}

/** Full scrubbed route: masked path + redacted query. */
export function sanitizeRoute(pathname: string, search = ""): string {
  return sanitizePath(pathname) + sanitizeSearch(search);
}

/**
 * Scrubbed referrer. Cross-origin referrers are reduced to their origin — we
 * have no route map for other sites, so their paths could be anything.
 */
export function sanitizeReferrer(referrer: string, origin: string): string {
  if (!referrer) return "";
  let url: URL;
  try {
    url = new URL(referrer);
  } catch {
    return "";
  }
  if (url.origin !== origin) return url.origin;
  return url.origin + sanitizeRoute(url.pathname, url.search);
}

/**
 * Page title. `document.title` is never used: on detail screens it can hold a
 * customer name or reference (B4). The scrubbed route is the title instead.
 */
export function pageTitleFor(route: string): string {
  return route;
}
